import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
    Box,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    LinearProgress,
    Paper,
    Rating,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { ThemeContext } from '../ThemeContext/ThemeContext';
import { LINHA_TODAS } from '../../utils/filtrosDoHistorico';
import http from '../../services/http';

/**
 * Os totais do histórico, agrupados nos três eixos pedidos.
 *
 * Gisele, 27/10/2026:
 *
 *  - 14: "agrupar e totalizar os atendimentos/avaliações por avaliação,
 *    equipe e responsável";
 *  - 15: "clicar num total abre o detalhe: as avaliações e os atendimentos
 *    correspondentes";
 *  - 16: o objetivo é analisar o resultado por canal, produto, equipe e
 *    responsável.
 *
 * ## Os números vêm do SERVIDOR, e o detalhe vem da mesma conta
 *
 * A lista do histórico que esta tela já mostrava é uma coisa; o resumo é
 * outra. Totalizar no navegador sobre a lista carregada daria um número que
 * depende do que chegou -- e agrupar por responsável exige ver o trabalho de
 * todos, que a lista (de um operador só) não tem.
 *
 * O detalhe sai do MESMO endpoint, com um recorte a mais. Montar a lista por
 * outro caminho faria o detalhe discordar do número que foi clicado: esta
 * base já pagou por isso ("o card diz 30, a lista mostra 18").
 */
const DIMENSOES = [
    { chave: 'avaliacao', titulo: 'Por avaliação', campo: 'por_avaliacao', comNotas: false },
    { chave: 'equipe', titulo: 'Por equipe', campo: 'por_equipe', comNotas: true },
    { chave: 'responsavel', titulo: 'Por responsável', campo: 'por_responsavel', comNotas: true },
];

const ResumoDoHistorico = ({ dateRange, userId, canal, linha, search }) => {
    const { theme } = useContext(ThemeContext);
    const [resumo, setResumo] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState(false);
    const [detalhe, setDetalhe] = useState(null);

    const filtros = useCallback(() => ({
        dateRange,
        user_id: userId,
        canal,
        /*
         * `LINHA_TODAS` é vocabulário da TELA, e não pode viajar para a api.
         *
         * Lá, "linha que não existe no cadastro" devolve ZERO de propósito --
         * deixar passar mostraria a base inteira como se o filtro tivesse
         * valido. Mandando a sentinela `'todas'`, o servidor procurava uma
         * linha de produto com esse nome, não achava, e o resumo vinha zerado
         * com a lista logo abaixo cheia de atendimentos.
         *
         * Medido em produção em 28/10/2026, no deploy deste resumo:
         * `linha: 'todas'` devolvia 0 e `linha: null` devolvia 7, com todo o
         * resto igual.
         *
         * O canal é outra história e vai cru: `'todos'` é constante do
         * servidor também (`Room::CANAL_TODOS`), e ele a entende.
         */
        linha: linha === LINHA_TODAS ? null : linha,
        search,
    }), [dateRange, userId, canal, linha, search]);

    useEffect(() => {
        if (!dateRange) return undefined;

        let cancelado = false;
        setCarregando(true);
        setErro(false);

        http.post('/history/resumo', filtros())
            .then((res) => {
                if (!cancelado) setResumo(res.data);
            })
            .catch(() => {
                /*
                 * Falha aqui não pode derrubar o histórico: a lista continua
                 * sendo o conteúdo principal da tela, e o resumo é um apoio.
                 */
                if (!cancelado) {
                    setResumo(null);
                    setErro(true);
                }
            })
            .finally(() => {
                if (!cancelado) setCarregando(false);
            });

        return () => { cancelado = true; };
    }, [dateRange, filtros]);

    const abrirDetalhe = (dimensao, grupo) => {
        setDetalhe({ dimensao, grupo, linhas: null, carregando: true });

        http.post('/history/resumo/detalhe', { ...filtros(), dimensao, chave: grupo.chave })
            .then((res) => setDetalhe((atual) => (
                atual ? { ...atual, linhas: res.data || [], carregando: false } : atual
            )))
            .catch(() => setDetalhe((atual) => (
                atual ? { ...atual, linhas: [], carregando: false } : atual
            )));
    };

    const media = (valor) => (valor == null ? '—' : String(valor).replace('.', ','));

    return (
        <Paper
            data-testid="resumo-do-historico"
            sx={{ p: 2, mb: 2, backgroundColor: theme.palette.background.paper }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: theme.palette.text.primary }}>
                    Resumo do período
                </Typography>
                {resumo && (
                    <>
                        <Chip size="small" label={`${resumo.total} atendimento(s)`} />
                        <Chip size="small" label={`${resumo.avaliados} avaliado(s)`} />
                        <Chip
                            size="small"
                            label={resumo.media == null ? 'Sem avaliação' : `Média ${media(resumo.media)}`}
                        />
                    </>
                )}
            </Box>

            <Box sx={{ height: 3, mb: 1 }}>
                {carregando && <LinearProgress data-testid="carregando-resumo" sx={{ height: 3 }} />}
            </Box>

            {erro && (
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    Não foi possível carregar o resumo. A lista abaixo continua valendo.
                </Typography>
            )}

            {resumo && (
                <Grid container spacing={2}>
                    {DIMENSOES.map((dimensao) => (
                        <Grid item xs={12} md={4} key={dimensao.chave}>
                            <Typography
                                variant="subtitle2"
                                sx={{ mb: 0.5, color: theme.palette.text.secondary }}
                            >
                                {dimensao.titulo}
                            </Typography>
                            <Table size="small" data-testid={`grupo-${dimensao.chave}`}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ color: theme.palette.text.secondary }}>
                                            {dimensao.chave === 'avaliacao' ? 'Nota' : 'Nome'}
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: theme.palette.text.secondary }}>
                                            Total
                                        </TableCell>
                                        {dimensao.comNotas && (
                                            <TableCell align="right" sx={{ color: theme.palette.text.secondary }}>
                                                Média
                                            </TableCell>
                                        )}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(resumo[dimensao.campo] || []).map((grupo) => (
                                        <TableRow key={`${dimensao.chave}-${grupo.chave}`} hover>
                                            <TableCell sx={{ color: theme.palette.text.primary }}>
                                                {grupo.rotulo}
                                            </TableCell>
                                            <TableCell align="right">
                                                {/*
                                                  * O total é o que se clica
                                                  * (ponto 15). Total zero não
                                                  * vira link: abrir uma lista
                                                  * vazia não responde nada.
                                                  */}
                                                {grupo.total > 0 ? (
                                                    <Tooltip title="Ver os atendimentos" arrow>
                                                        <Box
                                                            component="button"
                                                            type="button"
                                                            onClick={() => abrirDetalhe(dimensao.chave, grupo)}
                                                            sx={{
                                                                background: 'none',
                                                                border: 'none',
                                                                padding: 0,
                                                                cursor: 'pointer',
                                                                font: 'inherit',
                                                                textDecoration: 'underline',
                                                                color: theme.palette.primary.main,
                                                            }}
                                                        >
                                                            {grupo.total}
                                                        </Box>
                                                    </Tooltip>
                                                ) : (
                                                    <Typography
                                                        variant="body2"
                                                        component="span"
                                                        sx={{ color: theme.palette.text.secondary }}
                                                    >
                                                        0
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            {dimensao.comNotas && (
                                                <TableCell align="right" sx={{ color: theme.palette.text.primary }}>
                                                    {media(grupo.media)}
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Grid>
                    ))}
                </Grid>
            )}

            <Dialog
                open={Boolean(detalhe)}
                onClose={() => setDetalhe(null)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>{detalhe ? detalhe.grupo.rotulo : ''}</span>
                    <IconButton onClick={() => setDetalhe(null)} aria-label="Fechar">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {detalhe && detalhe.carregando && <LinearProgress />}
                    {detalhe && !detalhe.carregando && (detalhe.linhas || []).length === 0 && (
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                            Nenhum atendimento neste grupo.
                        </Typography>
                    )}
                    {detalhe && !detalhe.carregando && (detalhe.linhas || []).length > 0 && (
                        <Table size="small" data-testid="detalhe-do-grupo">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Cliente</TableCell>
                                    <TableCell>Responsável</TableCell>
                                    <TableCell>Equipe</TableCell>
                                    <TableCell>Avaliação</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {detalhe.linhas.map((item) => (
                                    <TableRow key={item.id}>
                                        <TableCell>{item.cliente || '—'}</TableCell>
                                        <TableCell>{item.responsavel}</TableCell>
                                        <TableCell>{item.equipe}</TableCell>
                                        <TableCell>
                                            {item.avaliacao > 0 ? (
                                                <Rating value={item.avaliacao} max={3} size="small" readOnly />
                                            ) : (
                                                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                                    Sem avaliação
                                                </Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>
            </Dialog>
        </Paper>
    );
};

export default ResumoDoHistorico;
